import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/destinations', label: 'Destinations' },
    { path: '/experiences', label: 'Experiences' },
    { path: '/how-it-works', label: 'How It Works' },
    { path: '/about', label: 'About' },
  ];

  return (
    <>
      {/* Floating Navigation */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="fixed top-0 left-0 right-0 z-50"
        data-testid="main-navigation"
      >
        <div 
          className={`
            px-6 md:px-12 lg:px-16 py-5
            flex items-center justify-between
            transition-all duration-500 ease-out
            ${isScrolled && !isMobileMenuOpen
              ? 'bg-[#f5f3ee]/95 backdrop-blur-md py-4 shadow-sm' 
              : 'bg-transparent'
            }
          `}
        >
          {/* Left side - Logo */}
          <Link to="/" className={`relative flex items-center gap-3 group ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`} data-testid="nav-logo">
            {/* New Logo Image */}
            <div className="relative flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_elegant-padel-1/artifacts/zjm8foo8_Padel%20Holidays%20%281%29.png"
                alt="Padel Holidays Algarve"
                className={`h-32 md:h-44 w-auto object-contain transition-all duration-300`}
                style={{ filter: isScrolled ? 'none' : 'brightness(0) invert(1)' }}
              />
            </div>
          </Link>

          {/* Center - Desktop Navigation */}
          <div className="hidden md:flex items-center">
            <div className="flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`
                    relative text-[13px] font-light tracking-[0.15em] uppercase
                    transition-all duration-300
                    ${location.pathname === link.path
                      ? isScrolled ? 'text-[#414b3b]' : 'text-[#f5f3ee]'
                      : isScrolled ? 'text-[#414b3b]/60 hover:text-[#414b3b]' : 'text-[#f5f3ee]/70 hover:text-[#f5f3ee]'
                    }
                  `}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                  {location.pathname === link.path && (
                    <motion.div
                      layoutId="nav-underline"
                      className={`absolute -bottom-1 left-0 right-0 h-px ${isScrolled ? 'bg-[#414b3b]' : 'bg-[#f5f3ee]'}`}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Right side - CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://wa.me/351968382742"
              target="_blank"
              rel="noopener noreferrer"
              className={`relative flex h-11 px-5 font-light text-[13px] tracking-[0.15em] uppercase items-center gap-2 transition-all duration-300 ${
                isScrolled 
                  ? 'bg-[#25D366] text-white hover:bg-[#128C7E]'
                  : 'bg-[#25D366] text-white hover:bg-[#128C7E]'
              }`}
              data-testid="nav-whatsapp-btn"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span>Talk to Us</span>
            </a>
            <Link
              to="/contact"
              className={`relative flex h-11 px-7 font-light text-[13px] tracking-[0.15em] uppercase items-center gap-2 transition-all duration-300 ${
                isScrolled 
                  ? 'border border-[#414b3b] text-[#414b3b] hover:bg-[#414b3b] hover:text-[#f5f3ee]'
                  : 'border border-[#f5f3ee]/50 text-[#f5f3ee] hover:bg-[#f5f3ee] hover:text-[#414b3b]'
              }`}
              data-testid="nav-book-btn"
            >
              <span>Book Now</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden relative w-10 h-10 flex items-center justify-center transition-colors z-50 ${isScrolled ? 'text-[#414b3b]' : 'text-[#f5f3ee]'}`}
            aria-label="Toggle mobile menu"
            data-testid="mobile-menu-toggle"
          >
            <motion.div
              animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
              transition={{ duration: 0.2 }}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.div>
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#414b3b] pt-40"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center h-full gap-8 -mt-20">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`
                      text-2xl font-light tracking-[0.2em] uppercase
                      transition-all duration-300 
                      ${location.pathname === link.path
                        ? 'text-[#8ca87c]'
                        : 'text-[#f5f3ee]/80 hover:text-[#8ca87c]'
                      }
                    `}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: navLinks.length * 0.1 }}
                className="mt-8"
              >
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="h-14 px-10 border border-[#8ca87c] text-[#f5f3ee] font-light text-sm tracking-[0.2em] uppercase flex items-center justify-center transition-all duration-300 hover:bg-[#8ca87c] hover:text-[#414b3b]"
                  data-testid="mobile-nav-book-btn"
                >
                  Book Your Trip
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (navLinks.length + 1) * 0.1 }}
              >
                <a
                  href="https://wa.me/351968382742"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="h-14 px-10 bg-[#25D366] text-white font-light text-sm tracking-[0.2em] uppercase flex items-center justify-center gap-3 transition-all duration-300 hover:bg-[#128C7E]"
                  data-testid="mobile-nav-whatsapp-btn"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Talk to Us
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
